export interface PersonalInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface VehicleInfo {
  make: string;
  model: string;
  year: number;
  vin: string;
  licensePlate: string;
  color: string;
  mileage: number;
  useType: 'personal' | 'commercial' | 'business';
}

export interface HomeInfo {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  homeType: 'house' | 'apartment' | 'condo' | 'townhouse';
  yearBuilt: number;
  squareFootage: number;
  bedrooms: number;
  bathrooms: number;
  stories: number;
  constructionType: 'wood' | 'brick' | 'concrete' | 'other';
  roofType: 'shingle' | 'tile' | 'metal' | 'other';
}

export interface InsuranceItem {
  id: string;
  name: string;
  description: string;
  value: number;
  category: 'electronics' | 'jewelry' | 'art' | 'furniture' | 'clothing' | 'other';
  purchaseDate: string;
  receipts: File[];
}

export interface QuoteRequest {
  id: string;
  type: 'auto' | 'home' | 'medical' | 'life' | 'business';
  personalInfo: Partial<PersonalInfo>;
  vehicleInfo?: Partial<VehicleInfo>;
  homeInfo?: Partial<HomeInfo>;
  items?: InsuranceItem[];
  status: 'draft' | 'submitted' | 'processing' | 'completed';
  createdAt: string;
  updatedAt: string;
}

export interface FormStep {
  step: number;
  title: string;
  description: string;
  isCompleted: boolean;
}

// Home Insurance Form Types

export type PersonType = 'individual' | 'entity';
export type PolicyholderRole = 'landlord' | 'tenant';
export type IdType = 'ine' | 'fm2' | 'fm3' | 'passport';
export type MaritalStatus = 'single' | 'married' | 'widowed' | 'divorced' | 'separated' | 'registeredPartnership';
export type PropertyType = 'house' | 'condominium';
export type PropertyUse = 'rental' | 'business' | 'personal';
export type CoverageType = 'contents' | 'building' | 'both';

export interface HomeInsurancePersonalInfo {
  personType: PersonType;
  // Individual fields
  policyholderRole?: PolicyholderRole;
  fullName: string;
  idType?: IdType;
  idFrontFile?: string; // File name for display
  idBackFile?: string; // File name for display (not for passport)
  maritalStatus?: MaritalStatus;
  nationality: string;
  occupation: string;
  email: string;
  dialCode: string;
  phoneNumber: string;
}

export interface HomeInsurancePropertyInfo {
  propertyType: PropertyType;
  address: string;
  proofOfAddressFile?: string; // File name for display
  nearSea: boolean | null; // Less than 500m from sea - required
  numberOfFloors: number | null; // required
  numberOfRooms: number | null; // required
  hasBasement: boolean | null; // required
  propertyUse: PropertyUse | null;
  wallsConcreteBlocks: boolean | null;
  wallsDetails: string; // If wallsConcreteBlocks is false
  ceilingsConcrete: boolean | null;
  ceilingsDetails: string; // If ceilingsConcrete is false
}

export interface HomeInsuranceCoverageInfo {
  constructionSquareMeters: number | null; // Optional
  coverageType: CoverageType | null;
  currency: string;
}

export interface HomeInsuranceFormData {
  personalInfo: HomeInsurancePersonalInfo;
  propertyInfo: HomeInsurancePropertyInfo;
  coverageInfo: HomeInsuranceCoverageInfo;
  currentStep: number;
  status: 'draft' | 'submitted';
  createdAt: string;
  updatedAt: string;
}

export const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Argentine', 'Armenian',
  'Australian', 'Austrian', 'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi', 'Barbadian',
  'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese', 'Bolivian', 'Bosnian', 'Brazilian',
  'British', 'Bruneian', 'Bulgarian', 'Burkinabe', 'Burmese', 'Burundian', 'Cambodian', 'Cameroonian',
  'Canadian', 'Cape Verdean', 'Central African', 'Chadian', 'Chilean', 'Chinese', 'Colombian',
  'Comorian', 'Congolese', 'Costa Rican', 'Croatian', 'Cuban', 'Cypriot', 'Czech', 'Danish',
  'Djiboutian', 'Dominican', 'Dutch', 'Ecuadorian', 'Egyptian', 'Emirati', 'Equatorial Guinean',
  'Eritrean', 'Estonian', 'Ethiopian', 'Fijian', 'Filipino', 'Finnish', 'French', 'Gabonese',
  'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian', 'Guatemalan', 'Guinean',
  'Guyanese', 'Haitian', 'Honduran', 'Hungarian', 'Icelandic', 'Indian', 'Indonesian', 'Iranian',
  'Iraqi', 'Irish', 'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese', 'Jordanian',
  'Kazakhstani', 'Kenyan', 'Kiribati', 'Korean', 'Kuwaiti', 'Kyrgyzstani', 'Laotian', 'Latvian',
  'Lebanese', 'Liberian', 'Libyan', 'Liechtensteiner', 'Lithuanian', 'Luxembourgish', 'Macedonian',
  'Malagasy', 'Malawian', 'Malaysian', 'Maldivian', 'Malian', 'Maltese', 'Marshallese', 'Mauritanian',
  'Mauritian', 'Mexican', 'Micronesian', 'Moldovan', 'Monacan', 'Mongolian', 'Montenegrin',
  'Moroccan', 'Mozambican', 'Namibian', 'Nauruan', 'Nepalese', 'New Zealander', 'Nicaraguan',
  'Nigerian', 'Nigerien', 'Norwegian', 'Omani', 'Pakistani', 'Palauan', 'Palestinian', 'Panamanian',
  'Papua New Guinean', 'Paraguayan', 'Peruvian', 'Polish', 'Portuguese', 'Qatari', 'Romanian',
  'Russian', 'Rwandan', 'Saint Lucian', 'Salvadoran', 'Samoan', 'Saudi', 'Senegalese', 'Serbian',
  'Seychellois', 'Sierra Leonean', 'Singaporean', 'Slovak', 'Slovenian', 'Solomon Islander',
  'Somali', 'South African', 'South Sudanese', 'Spanish', 'Sri Lankan', 'Sudanese', 'Surinamese',
  'Swazi', 'Swedish', 'Swiss', 'Syrian', 'Taiwanese', 'Tajikistani', 'Tanzanian', 'Thai', 'Togolese',
  'Tongan', 'Trinidadian', 'Tunisian', 'Turkish', 'Turkmen', 'Tuvaluan', 'Ugandan', 'Ukrainian',
  'Uruguayan', 'Uzbekistani', 'Vanuatuan', 'Venezuelan', 'Vietnamese', 'Yemeni', 'Zambian', 'Zimbabwean'
];

export const DIAL_CODES = [
  { code: '+52', country: 'Mexico' },
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+34', country: 'Spain' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+39', country: 'Italy' },
  { code: '+81', country: 'Japan' },
  { code: '+86', country: 'China' },
  { code: '+91', country: 'India' },
  { code: '+55', country: 'Brazil' },
  { code: '+54', country: 'Argentina' },
  { code: '+57', country: 'Colombia' },
  { code: '+56', country: 'Chile' },
  { code: '+51', country: 'Peru' },
  { code: '+58', country: 'Venezuela' },
  { code: '+593', country: 'Ecuador' },
  { code: '+502', country: 'Guatemala' },
  { code: '+503', country: 'El Salvador' },
  { code: '+504', country: 'Honduras' },
  { code: '+505', country: 'Nicaragua' },
  { code: '+506', country: 'Costa Rica' },
  { code: '+507', country: 'Panama' },
  { code: '+53', country: 'Cuba' },
  { code: '+1809', country: 'Dominican Republic' },
];
